import { FC, useEffect, useMemo, useState, useCallback } from "react";

import DashboardContentLayout from "../components/dashboard-content-layout";
import { SortOptions } from "../components/generic-table/generic-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePersistedSearchParams } from "../../../hooks/use-persisted-search-params";
import {
  Nullable,
  useAdminRegionV2ControllerDeleteRegionById,
  useAdminLocationV2ControllerGetAllLocationsPaginated,
  useAdminRegionV2ControllerDeleteRegionLocations,
  useAdminRegionV2ControllerUpdateRegionServiceTime,
  useAdminRegionV2ControllerDeleteLocationsFromManyRegions,
  useAdminRegionV2ControllerTriggerEstCalculationForRegion,
  useAdminRegionV2ControllerUpdateRegion,
  useAdminRegionV2ControllerUpdateRegionPriority,
} from "service";
import RegionManagementTable from "./region-management-table";
import useRegionManagementStore from "../../../state/region-management-store";
import MapComponent from "../../../components/map/map";
import {
  Box,
  Button,
  CircularProgress,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import {
  ControlledSelect,
  ControlledSelectOption,
  generateRoute,
  makeSxStyles,
} from "ui";
import {
  MapActionTypeEnum,
  mapActionTypeEnumLabelMap,
} from "service/types/enum/action-type-enum";
import { AdRoutes } from "../../../constants/ad-routes";
import useRegions from "../../../hooks/use-regions";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { toast } from "react-toastify";
import { AdminAllLocationsResDto } from "service/api/estabrookSchemas";
import { useDialogStore } from "../../../state/dialog-store";
import { queryClient } from "../../../query-client";
import { createRegionsInvalidationPredicate } from "service/api/estabrookQueryKeys";
import { RegionMapFilters } from "./map-filters/map-filters";
import SelectedAddresesSidebar from "../../../components/map/selected-addresses-sidebar";
import { merge } from "lodash";
import ServiceDurationDialogContainer, {
  adminPutRegionServiceTimeReqDtoCustomSchema,
} from "./change-service-time";
import {
  MapComponentProps,
  MapMarker,
} from "../../../components/map/map.types";
import { CustomMoveLocationsDialog } from "./custom-move-dialog/custom-move-dialog";
import MoveLocationsDialog from "./move-locations-dialog";
import MapProvider from "../../../components/map/map-provider";
import SocketContextProvider from "../../../web-sockets/web-socket-provider";
import { useAddressClickHandler } from "../../../shared/hooks/use-address-click-handler";
import Navbar from "../../../components/navbar/navbar";
import useMapResizing from "../../../shared/hooks/useMapResizing";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import HeaderStandardActions from "../../../shared/components/header-items/HeaderStandardActions";
import { TurnSharpRightOutlined } from "@mui/icons-material";

export type AdminPostServiceTimeDto = {
  serviceTypeChange?: string;
  percentage: number;
};

// Add transformation function
const transformAdminLocationsToMapMarkers = (
  locations: AdminAllLocationsResDto[],
): MapMarker[] => {
  return locations.map((location) => ({
    id: location.id,
    latitude: location.latitude,
    longitude: location.longitude,
    address: location.address,
    locationSequence:
      typeof location.locationSequence === "number"
        ? location.locationSequence
        : location.locationSequence === null ||
            location.locationSequence === undefined
          ? null
          : Number(location.locationSequence) || null,
    customer: location.customer,
    region: location.region
      ? {
          ...location.region,
          color:
            typeof location.region.color === "string"
              ? location.region.color
              : undefined,
        }
      : undefined,
    // Add duration from serviceTime for hover tooltip
    duration: location.serviceTime,
    customerType: location.type,
    locationNoteCount: location.locationNoteCount,
  }));
};

const RegionManagementPage: FC = () => {
  const styles = useStyles();
  const {
    isLoading: isLoadingRegions,
    data: regionData,
    refetch: refetchRegions,
  } = useRegions({});
  const [params] = useSearchParams();
  const navigate = useNavigate();

  // Persist search params for breadcrumb navigation
  usePersistedSearchParams();
  const { setDialog } = useDialogStore();
  const [isSerOpen, setIsSerOpen] = useState<boolean>(false);
  const [isRegionId, setRegionId] = useState<string>("");
  const [isMovOpen, setIsMoveOpen] = useState<boolean>(false);
  const serviceDuration: ControlledSelectOption[] = [
    {
      label: "Increase",
      value: "increase",
    },
    {
      label: "Reduce",
      value: "reduce",
    },
  ];

  const {
    selectedAddresses,
    sidebar,
    addresses,
    query,
    flatRate,
    onDemand,
    unassignedOrders,
    unassignedAddresses,
    validSequence,
    sameRegionId,
    drawing,
    setFlatRate,
    setOnDemand,
    setUnassignedOrders,
    mapZoom,
    mapCentering,
    setQuery,
    setMapZoom,
    setSidebar,
    mapHeight,
    regionSequencing,
    lastSequence,
    setAddresses,
    toggleDrawing,
    setAddressSequence,
    resetAllSelected,
    setDirectSelection,
    findRegionAddresses,
    findAddress,
    removeSelected,
    setRegionSequence,
    setRegionSequencing,
    setSelectedAddress,
    setMapCentering,
    setMapHeight,
    setActiveRegionShortDetails,
    recalcEstStatus,
    setRegions,
    pendingFocusRegionId,
    setPendingFocusRegionId,
    setActiveTableRowIndex,
  } = useRegionManagementStore();

  // Compute query params based on current filter state
  const queryParams = useMemo(() => {
    const params: Record<string, string | string[] | boolean> = {};

    // Handle type filter (flat rate = "annual", on demand = "onDemand")
    const selectedTypes: string[] = [];
    if (flatRate) selectedTypes.push("annual");
    if (onDemand) selectedTypes.push("onDemand");

    if (selectedTypes.length > 0) {
      params.type = selectedTypes;
    }

    // Handle assigned filter (unassigned orders = assigned: false)
    if (unassignedOrders) {
      params.assigned = false;
    }

    return params;
  }, [flatRate, onDemand, unassignedOrders]);

  const {
    refetch: refetchAll,
    isLoading,
    data,
  } = useAdminLocationV2ControllerGetAllLocationsPaginated({
    queryParams,
  });

  const { onMouseDown, isDragging } = useMapResizing({
    minHeight: 70,
    maxHeight: window.innerHeight - 190, // Account for navbar (120px), drag handle (~30px), and minimum table space (~100px)
    setHeight: setMapHeight,
  });

  const { mutate: unassignLocations } =
    useAdminRegionV2ControllerDeleteRegionLocations({
      onSuccess: async () => {
        resetAllSelected(); // Reset selection before data refetch to prevent re-applying selection state
        toast.success(
          "Locations Unassigned Successfully. Future Routes have been modified to the new Region Template. Live Route are unaffected.",
        );
        await refetchAll();
        await refetchRegions();
      },
    });

  const { mutate: unassignLocationsFromMultipleRegions } =
    useAdminRegionV2ControllerDeleteLocationsFromManyRegions({
      onSuccess: async () => {
        resetAllSelected(); // Reset selection before data refetch to prevent re-applying selection state
        toast.success(
          "Locations Unassigned Successfully. Future Routes have been modified to the new Region Template. Live Route are unaffected.",
        );
        await refetchAll();
        await refetchRegions();
      },
    });

  const {
    isLoading: isLoadingSearchAddresses,
    isFetching: isFetchingSearchAddresses,
    data: searchData,
  } = useAdminLocationV2ControllerGetAllLocationsPaginated(
    {
      queryParams: {
        ...queryParams,
        search: query,
      },
    },
    {
      enabled: query.length > 3,
    },
  );

  const { mutate: updateRegion } = useAdminRegionV2ControllerUpdateRegion({
    onSuccess: async () => {
      toast.success("Region updated successfully");
      await refetchRegions();
    },
    onError: () => {
      toast.error("Failed to update region");
    },
  });

  const { mutate: updateRegionStartTime } =
    useAdminRegionV2ControllerUpdateRegion({
      onSuccess: async () => {
        toast.success("Example Start Time Updated");
        await refetchRegions();
      },
      onError: () => {
        toast.error("Failed to update start time");
      },
    });

  const { mutate: updateRegionPriority } =
    useAdminRegionV2ControllerUpdateRegionPriority({
      onSuccess: async () => {
        toast.success("Region order updated successfully");
        await refetchRegions();
      },
      onError: () => {
        toast.error("Failed to update region order");
      },
    });

  const { mutate: updateServiceTime } =
    useAdminRegionV2ControllerUpdateRegionServiceTime({
      onSuccess: async () => {
        toast.success("Changed service time Successfully");
        queryClient.invalidateQueries(createRegionsInvalidationPredicate());
        await refetchAll();
        refetchRegions();
        resetForm();
      },
    });

  const { mutate: deleteRegion } = useAdminRegionV2ControllerDeleteRegionById({
    onSuccess: () => {
      toast.success("Region Deleted Successfully");
      queryClient.invalidateQueries(createRegionsInvalidationPredicate());
      setTimeout(() => {
        refetchAll();
        refetchRegions();
      }, 1000);
    },
  });

  const { mutate: recalculateEST } =
    useAdminRegionV2ControllerTriggerEstCalculationForRegion({
      onSuccess: () => {
        toast.success("Regions EST calculation is in progress...");
      },
    });

  useEffect(() => {
    if (data && data?.data && !isLoading) {
      setAddresses(transformAdminLocationsToMapMarkers(data.data));
    }
  }, [isLoading, data, setAddresses]);

  // Separate effect to handle pending region focus - runs on every data update
  useEffect(() => {
    if (pendingFocusRegionId && regionData?.data && addresses.length > 0) {
      // Find the index of the created region in the regions array
      const regionIndex = regionData.data.findIndex(
        (region) => region.id === pendingFocusRegionId,
      );

      if (regionIndex !== -1) {
        // Check if this region has addresses assigned
        const regionAddresses = addresses.filter(
          (address) => address.region?.id === pendingFocusRegionId,
        );

        if (regionAddresses.length > 0) {
          // Focus on the region and table row
          setMapCentering(pendingFocusRegionId);
          setActiveTableRowIndex(regionIndex);
          setPendingFocusRegionId(null);
        }
      }
    }
  }, [
    pendingFocusRegionId,
    regionData?.data,
    addresses,
    setMapCentering,
    setActiveTableRowIndex,
    setPendingFocusRegionId,
  ]);

  // Initialize filters from URL params on component mount
  useEffect(() => {
    const urlTypes = params.getAll("type");
    const urlAssigned = params.get("assigned");

    // Initialize filter states based on URL params
    setFlatRate(urlTypes.includes("annual"));
    setOnDemand(urlTypes.includes("onDemand"));
    setUnassignedOrders(urlAssigned === "false");
  }, []); // Only run on mount

  const handleQueryParams = useCallback(() => {
    const params = new URLSearchParams();

    // Handle type filter (flat rate = "annual", on demand = "onDemand")
    const selectedTypes: string[] = [];
    if (flatRate) selectedTypes.push("annual");
    if (onDemand) selectedTypes.push("onDemand");

    if (selectedTypes.length > 0) {
      selectedTypes.forEach((type) => params.append("type", type));
    }

    // Handle assigned filter (unassigned orders = assigned: false)
    if (unassignedOrders) {
      params.set("assigned", "false");
    }

    // Update the URL
    navigate({ search: params.toString() }, { replace: true });
  }, [flatRate, onDemand, unassignedOrders, navigate]);

  // Update URL when filters change
  useEffect(() => {
    handleQueryParams();
  }, [handleQueryParams]);

  const unassignedAddressCount: number = addresses.filter(
    (address) => !address.region,
  ).length;

  const mapActionTypeSelectOptions = useMemo(() => {
    return Object.values(MapActionTypeEnum).map((c) => ({
      label: mapActionTypeEnumLabelMap[c],
      value: c,
      disabled: unassignedAddresses
        ? c === MapActionTypeEnum.unassign ||
          c === MapActionTypeEnum.changeService
        : !unassignedAddresses
          ? c === MapActionTypeEnum.newRegion
          : false,
    }));
  }, [selectedAddresses]);

  const onActionType = (type: MapActionTypeEnum) => {
    toggleDrawing(false);

    if (type === MapActionTypeEnum.sendNotification) {
      handleSendNotification();
      return;
    }

    if (type === MapActionTypeEnum.newRegion) {
      // Clear visual selection state on both addresses and selectedAddresses arrays
      const updatedAddresses = addresses.map((address) => ({
        ...address,
        selected: false,
      }));
      const updatedSelectedAddresses = selectedAddresses.map((address) => ({
        ...address,
        selected: false,
      }));
      setAddresses(updatedAddresses);
      setDirectSelection(updatedSelectedAddresses);

      navigate(AdRoutes.createSequence);
    } else if (type === MapActionTypeEnum.moveToRegion) {
      setIsMoveOpen(true);
    } else if (type === MapActionTypeEnum.unassign) {
      const selectedStringIds: string[] = [];
      selectedAddresses.map((address) => {
        selectedStringIds.push(address.id);
      });

      return setDialog({
        title: "Unassign Locations",
        description: `Are you sure you want to unassign these locations?`,
        confirmText: "Unassign",
        cancelText: "Cancel",
        withCancelAction: true,
        onConfirm: async (onClose) => {
          if (!sameRegionId) {
            unassignLocationsFromMultipleRegions({
              body: {
                locations: selectedStringIds,
              },
            });
          } else {
            unassignLocations({
              pathParams: {
                id: selectedAddresses[0].region?.id ?? "",
              },
              body: {
                locations: selectedStringIds,
              },
            });
          }
          onClose();
        },
      });
    } else {
      setRegionId(selectedAddresses[0].region?.id ?? "");
      return setIsSerOpen(true);
    }
  };

  const handleSendNotification = () => {
    if (!selectedAddresses.length) {
      toast.error("No selected addresses found!");
      return;
    }

    // Get all customer-location pairs from selected addresses
    // Include ALL selected addresses, even if multiple addresses belong to the same customer
    const customerLocationPairs: Array<{
      customerId: string;
      locationId: string;
    }> = [];

    selectedAddresses.forEach((address) => {
      if (address.customer?.id && address.id) {
        customerLocationPairs.push({
          customerId: address.customer.id,
          locationId: address.id,
        });
      }
    });

    if (customerLocationPairs.length === 0) {
      toast.error("No valid customers found in selected addresses!");
      return;
    }

    // Navigate to Create Notification screen with pre-selected recipients
    // Format: customerId:locationId,customerId:locationId,...
    // Multiple addresses for the same customer will result in multiple recipient entries
    const recipientsParam = customerLocationPairs
      .map(({ customerId, locationId }) => `${customerId}:${locationId}`)
      .join(",");

    const params = new URLSearchParams();
    params.set("recipients", recipientsParam);
    navigate(`${AdRoutes.addNotification}?${params.toString()}`);
  };

  const { setClickedAddress } = useAddressClickHandler();

  const [sortOptions, setSortOptions] = useState<Nullable<SortOptions>>(
    params.get("sortBy")
      ? {
          sortBy: params.get("sortBy") || "",
          direction: (params.get("direction") || "") as "asc" | "desc",
        }
      : null,
  );

  useEffect(() => {
    if (!isLoadingRegions && regionData?.data?.length) {
      // Only set map height on initial load, not on subsequent data updates
      // This preserves user's manual drag handle adjustments
      const currentMapHeight = mapHeight;
      const isInitialLoad = currentMapHeight === "450px"; // Default from store

      if (isInitialLoad) {
        setMapHeight(
          // TODO: Update map height logic for new filters
          regionData?.data?.length ? "350px" : "calc(100vh - 180px)",
        );
      }

      setRegions(regionData.data);
    }
  }, [
    regionData,
    /* unassigned, assigned */ flatRate,
    onDemand,
    unassignedOrders,
    mapHeight, // Add mapHeight to dependencies to check current value
  ]);

  const handleSequenceChange = (id: string) => {
    const regionDetails = regionData?.data?.find((region) => region.id === id);
    setRegionSequence(id);
    setRegionSequencing(id);
    setActiveRegionShortDetails({
      color: regionDetails?.color || "",
      name: regionDetails?.name || "",
    });
  };

  useEffect(() => {
    if (regionSequencing !== "" && selectedAddresses.length > 0) {
      navigate(
        generateRoute(AdRoutes.resequenceRoute, {
          regionId: regionSequencing || "",
        }),
      );
    }
  }, [regionSequencing]);

  const handleServiceDurationChange = (id: string) => {
    setRegionId(id);
    findRegionAddresses(id);
    setIsSerOpen(true);
  };
  const onRowMapCenter = (val: string) => {
    setMapCentering(val);
  };

  const {
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    submitForm,
    setFieldTouched,
    setFieldValue,
    resetForm,
    isValid,
    dirty,
    validateForm,
  } = useFormik<AdminPostServiceTimeDto>({
    initialValues: {
      serviceTypeChange: "",
      percentage: 1,
    },
    validationSchema: toFormikValidationSchema(
      adminPutRegionServiceTimeReqDtoCustomSchema,
    ),
    onSubmit: () => {
      updateServiceTime({
        pathParams: { id: isRegionId },
        body: {
          percentage:
            values.serviceTypeChange === "reduce"
              ? -values.percentage
              : values.percentage,
        },
      });
      setIsSerOpen(false);
    },
  });

  // Stable identity (store actions are stable) so the marker components keep
  // their memoization and only the markers whose selection changed re-render.
  const handleMarkerClick = useCallback(
    (marker: MapMarker, isShiftPressed: boolean) => {
      const address = findAddress(marker.id);
      if (address) {
        setSelectedAddress([address], isShiftPressed);
      }
    },
    [findAddress, setSelectedAddress],
  );

  const handleSelectedMarkersChange = useCallback(
    (selectedMarkers: MapMarker[]) => {
      setDirectSelection(selectedMarkers);
      setSidebar(selectedMarkers.length > 0);
      // Zoom/center to fit the lassoed selection. This is safe now that
      // fitMapToMarkers no longer snaps to the world view first (that snap was
      // the cause of the disappear/reappear flash, not the zoom itself).
      if (selectedMarkers.length > 0) {
        setMapCentering(undefined, selectedMarkers);
      }
    },
    [setDirectSelection, setSidebar, setMapCentering],
  );

  const updateRegionStartTimeHandler = (
    regionId: string,
    startTime: string,
  ) => {
    const region = regionData?.data?.find((r) => r.id === regionId);
    if (!region) {
      toast.error("Region not found");
      return;
    }

    // Preserve the existing driver when updating start time
    const existingDriverId = region.suggestedDrivers?.[0]?.driverId;
    const suggestedDrivers = existingDriverId
      ? { dailyShiftDriverId: existingDriverId }
      : undefined;

    updateRegionStartTime({
      pathParams: { id: regionId },
      body: {
        name: region.name,
        color: region.color,
        startTime: startTime,
        suggestedDrivers: suggestedDrivers,
      },
    });
  };

  const updateRegionDriver = (regionId: string, driverId: string) => {
    const region = regionData?.data?.find((r) => r.id === regionId);
    if (!region) {
      toast.error("Region not found");
      return;
    }

    // Handle special driver values
    let suggestedDrivers;
    if (driverId === "Any" || driverId === "No Driver") {
      // For "Any" or "No Driver", we might need to remove the driver assignment
      // Check if we should pass null/undefined or a specific value
      suggestedDrivers = undefined;
    } else {
      // For specific driver ID
      suggestedDrivers = {
        dailyShiftDriverId: driverId,
      };
    }

    updateRegion({
      pathParams: { id: regionId },
      body: {
        name: region.name,
        color: region.color,
        startTime: region.startTime,
        suggestedDrivers: suggestedDrivers,
      },
    });
  };

  const mapProps: MapComponentProps = {
    mapWidth: "100%",
    mapHeight,
    markers: addresses,
    mapZoom,
    mapCentering,
    drawing,
    query,
    isLoading: isLoading,
    isSearchLoading: isLoadingSearchAddresses && isFetchingSearchAddresses,
    searchedAddresses: searchData?.data
      ? transformAdminLocationsToMapMarkers(searchData.data)
      : undefined,
    setQuery,
    onDrawingToggle: toggleDrawing,
    onMapZoomChange: setMapZoom,
    setSelectedAddress,
    lastSequence,
    setAddressSequence,
    setSidebar,
    selectedAddresses,
    setMapCentering,
    onMarkerClick: handleMarkerClick,
    onSelectedMarkersChange: handleSelectedMarkersChange,
    onResetSelection: resetAllSelected,
    mapFilters: (
      <RegionMapFilters
        onFlatRateChange={setFlatRate}
        onOnDemandChange={setOnDemand}
        onUnassignedOrdersChange={setUnassignedOrders}
        flatRate={flatRate}
        onDemand={onDemand}
        unassignedOrders={unassignedOrders}
      />
    ),
  };

  const showRecalcEstStatusIcon = () => {
    if (recalcEstStatus === "started") {
      return <CircularProgress color="inherit" size="20px" />;
    }

    return <TurnSharpRightOutlined />;
  };

  return (
    <SocketContextProvider currentPage="region-management">
      <DashboardContentLayout flexedContainer={sidebar} hasBottomPadding>
        <Navbar showAccountMenu>
          <HeaderItems>
            <HeaderItemsLeft
              breadcrumbs={[
                {
                  label: "Region Management",
                  url: AdRoutes.regionManagement,
                },
              ]}
            />
            <HeaderItemsRight>
              <HeaderStandardActions
                onRefresh={refetchAll}
                customActions={
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setMapCentering(
                          undefined,
                          addresses.filter((address) => !address.region),
                        );
                      }}
                    >
                      {`Unassigned Addresses (${unassignedAddressCount})`}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => recalculateEST({})}
                      startIcon={showRecalcEstStatusIcon()}
                    >
                      Recalculate EST
                    </Button>
                  </>
                }
              />
            </HeaderItemsRight>
          </HeaderItems>
        </Navbar>

        <Box
          sx={
            {
              ...styles.resizableContentWrapper,
              ...(sidebar ? styles.hasSidebar : styles.noSidebar),
            } as SxProps<Theme>
          }
        >
          <MapProvider>
            <MapComponent {...mapProps} />
          </MapProvider>
          <Box sx={styles.dragHandleWrapper} onMouseDown={onMouseDown}>
            <Box
              className="drag-handle"
              sx={merge(styles.dragHandler, isDragging && styles.onDragStyle)}
            />
          </Box>
          {
            /* TODO: Update condition for new filters */ regionData?.data
              ?.length ? (
              <RegionManagementTable
                data={regionData?.data}
                sortOptions={sortOptions}
                onDeleteRegion={deleteRegion}
                sortOptionsChange={setSortOptions}
                onClickedAddress={setClickedAddress}
                onRegionResequence={handleSequenceChange}
                onChangeServiceDuration={handleServiceDurationChange}
                handleRowClick={(val: string) => onRowMapCenter(val)}
                onSelectRegionDriver={(regionId, driverId) => {
                  updateRegionDriver(regionId, driverId);
                }}
                onSelectStartTime={(regionId, startTime) => {
                  updateRegionStartTimeHandler(regionId, startTime);
                }}
                onUpdateRegionPriority={(regionId, priority) => {
                  updateRegionPriority({
                    pathParams: { id: regionId },
                    body: { priority },
                  });
                }}
                isLoading={false}
              />
            ) : (
              ""
            )
          }
        </Box>
        {sidebar && (
          <SelectedAddresesSidebar
            customHeader={
              <>
                <Box sx={styles.sideBarTitleInfo}>
                  <Typography fontStyle="700" fontSize="16px" lineHeight="24px">
                    {selectedAddresses && selectedAddresses.length} selected
                  </Typography>
                  <Button onClick={resetAllSelected}>Reset</Button>
                </Box>
                <Box>
                  <ControlledSelect
                    placeholderSelectEnabled={false}
                    placeholdeShow={false}
                    placeholder="Actions"
                    options={mapActionTypeSelectOptions}
                    sx={{ width: "100%" }}
                    onChange={(value) => {
                      onActionType(value as MapActionTypeEnum);
                    }}
                    data-testid="action-button"
                  />
                </Box>
              </>
            }
            invalidSelection={false}
            selectedAddresses={selectedAddresses}
            removeSelected={removeSelected}
            view="region-management"
          />
        )}
        <CustomMoveLocationsDialog
          title="Change Service Duration"
          onConfirm={submitForm}
          open={isSerOpen}
          cancelText="Cancel"
          confirmText="Update Duration"
          confirmButtonDisabled={!isValid || !dirty}
          sx={styles.serviceDurationDialogWidth}
          children={
            <ServiceDurationDialogContainer
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              serviceDuration={serviceDuration}
              validateForm={validateForm}
            />
          }
          onClose={() => setIsSerOpen(false)}
        />
        <MoveLocationsDialog
          open={isMovOpen}
          onClose={() => setIsMoveOpen(false)}
          onRefresh={async () => {
            await refetchRegions();
            await queryClient.invalidateQueries(
              createRegionsInvalidationPredicate(),
            );
          }}
          shouldSequence={
            selectedAddresses.length > 1 && (!validSequence || !sameRegionId)
          }
          regions={regionData?.data || []}
        />
      </DashboardContentLayout>
    </SocketContextProvider>
  );
};

const useStyles = makeSxStyles(({ palette }) => ({
  resizableContentWrapper: {
    minHeight: "calc(100vh - 90px)",
    maxHeight: "calc(100vh - 90px)",
    display: "flex",
    flexDirection: "column",
  },
  hasSidebar: {
    width: "calc(100% - 317px)",
  },
  sideBarTitleInfo: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dragHandler: {
    height: "4px",
    width: "40px",
    borderRadius: "4px",
    backgroundColor: palette.grey[300],
    margin: "6px auto 0",
    cursor: "grabbing",
  },
  onDragStyle: {
    backgroundColor: "#fff",
  },
  noSidebar: {
    width: "100%",
  },
  moveDialogWidth: {
    "& .MuiPaper-root": {
      maxWidth: "820px",
    },
  },
  serviceDurationDialogWidth: {
    "& .MuiPaper-root": {
      minWidth: "500px",
      maxWidth: "820px",
    },
    "& .MuiDialogContent-root": {
      overflowY: "visible",
    },
    "& .Mui-error": {
      fontSize: "0.6rem",
    },
  },
  topActions: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  dragHandleWrapper: {
    width: "100%",
    cursor: "grabbing",
  },
}));

export default RegionManagementPage;
